import { StrictMode } from 'react';
import { createRoot, hydrateRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { GoogleOAuthProvider } from '@react-oauth/google';
import App from './App.tsx';

const queryClient = new QueryClient();
const googleClientId = import.meta.env.VITE_GOOGLE_CLIENT_ID || '';

// 배포 직후, 이전 번들이 떠 있는 탭은 사라진 청크를 lazy import 하다 실패한다.
// (예: 회원가입 완료 후 화면 전환 → "Failed to fetch dynamically imported module")
// Vite 가 이때 window 에 vite:preloadError 를 쏘므로 받아서 한 번만 새로고침한다.
// sessionStorage 가드: 진짜로 파일이 없는 경우 무한 새로고침에 빠지지 않게 1회로 제한.
const RELOAD_GUARD = 'chunk-reload-attempted';
window.addEventListener('vite:preloadError', (event) => {
  if (sessionStorage.getItem(RELOAD_GUARD)) {
    console.error('[chunk] 새로고침 후에도 청크 로드 실패 — 자동 복구 중단', event);
    return;
  }
  sessionStorage.setItem(RELOAD_GUARD, '1');
  console.warn('[chunk] 배포로 청크가 교체된 것으로 보임 — 새로고침');
  window.location.reload();
});
// 정상 로드까지 왔으면 가드 해제 (다음 배포 때 다시 1회 쓸 수 있도록)
window.addEventListener('load', () => sessionStorage.removeItem(RELOAD_GUARD));

if (!googleClientId) {
  console.warn('⚠️ VITE_GOOGLE_CLIENT_ID가 설정되지 않았습니다. 구글 로그인 기능이 작동하지 않을 수 있습니다.');
}

const rootElement = document.getElementById('root')!;

const AppWithProviders = () => (
  <StrictMode>
    {googleClientId ? (
      <GoogleOAuthProvider clientId={googleClientId}>
        <QueryClientProvider client={queryClient}>
          <App />
        </QueryClientProvider>
      </GoogleOAuthProvider>
    ) : (
      <QueryClientProvider client={queryClient}>
        <App />
      </QueryClientProvider>
    )}
  </StrictMode>
);

// react-snap 프리렌더링 지원: HTML이 이미 있으면 hydrate, 없으면 render
if (rootElement.hasChildNodes()) {
  hydrateRoot(rootElement, <AppWithProviders />);
} else {
  createRoot(rootElement).render(<AppWithProviders />);
}

// LCP 최적화: React 렌더링 완료 후 prerender-hero 숨김
if (typeof window !== 'undefined') {
  const hidePrerender = () => {
    document.querySelectorAll('.prerender-hero').forEach((el) => {
      el.classList.add('hidden');
    });
  };

  if ('requestIdleCallback' in window) {
    requestIdleCallback(hidePrerender, { timeout: 1000 });
  } else {
    setTimeout(hidePrerender, 100);
  }
}
